import { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { useNavigate } from "react-router-dom";
import { Download, Loader2, Gamepad2, Settings, AlertTriangle, Network, Copy, Check, ShieldCheck, ShieldAlert } from "lucide-react";
import { serverStore } from "../../stores/server.store";
import { Card, CardHeader, CardTitle, CardContent } from "../design/Card";
import { Button } from "../design/Button";
import { SteamModBrowser } from "../server/SteamModBrowser";
import toast from "react-hot-toast";
import { joinAddress } from "../../utils/join-address";

const heroFor = (appId?: number | null) =>
  appId ? `https://cdn.cloudflare.steamstatic.com/steam/apps/${appId}/header.jpg` : null;

const PROTO_LABEL: Record<string, string> = { both: "TCP + UDP", udp: "UDP", tcp: "TCP" };
const PURPOSE_LABEL: Record<string, string> = { game: "Spiel", query: "Serverbrowser/Query", rcon: "RCON" };

/** "Verbinden & Ports": Join-Adresse, Router-Freigaben + Host-Firewall-Check
 *  (firewalld) — zeigt bei fehlenden Ports einen Ein-Klick-Kopierbefehl. */
const ConnectCard = ({ game, serverId, serverPort }: { game: any; serverId: string; serverPort: number | null }) => {
  const [copied, setCopied] = useState(false);
  const [fw, setFw] = useState<any>(null);
  const [cmdCopied, setCmdCopied] = useState(false);
  const ports: any[] = game?.defaultPorts || [];
  const host = window.location.hostname;
  const primary = ports.find((p) => p.purpose === "game") || ports[0];
  const joinPort = serverPort || primary?.port;
  const address = joinAddress(host, joinPort, primary?.port);
  const portShifted = serverPort && ports.length > 0 && serverPort !== ports[0].port;

  useEffect(() => {
    let alive = true;
    serverStore.fetchFirewall(serverId).then((d) => { if (alive) setFw(d); });
    return () => { alive = false; };
  }, [serverId]);

  const copyText = async (text: string, setter: (v: boolean) => void) => {
    try {
      await navigator.clipboard.writeText(text);
      setter(true);
      setTimeout(() => setter(false), 1500);
    } catch {
      toast.error("Kopieren nicht möglich — Text manuell markieren.");
    }
  };

  return (
    <Card className="bg-ink border-line-strong text-white" noPadding>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2"><Network size={17} className="text-ember" /> Verbinden & Ports</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <div className="text-xs text-fg-muted mb-1">Server-Adresse (im Spiel „Verbinden mit IP" / Direct Connect)</div>
          <div className="flex items-center gap-2">
            <code className="flex-1 rounded-lg bg-white/[0.04] border border-line px-3 py-2 font-mono text-sm text-fg select-all">{address}</code>
            <Button variant="secondary" onClick={() => copyText(address, setCopied)} title="Adresse kopieren">
              {copied ? <Check size={15} className="text-online" /> : <Copy size={15} />}
            </Button>
          </div>
        </div>

        {ports.length > 0 && (
          <div>
            <div className="text-xs text-fg-muted mb-1.5">Portfreigaben im Router (für Beitritt über das Internet)</div>
            <div className="flex flex-wrap gap-1.5">
              {ports.map((p) => (
                <span key={`${p.port}-${p.protocol}`} className="rounded-md bg-white/[0.05] border border-line px-2 py-1 text-xs font-mono text-fg">
                  {p.port} <span className="text-fg-muted">{PROTO_LABEL[p.protocol] || p.protocol}</span>
                  {p.purpose && <span className="text-fg-muted"> · {PURPOSE_LABEL[p.purpose] || p.purpose}</span>}
                </span>
              ))}
            </div>
            {portShifted && (
              <p className="text-[11px] text-warn mt-1.5">
                Der Server nutzt Port {serverPort} statt Standard {ports[0].port} — Freigaben entsprechend verschieben.
              </p>
            )}
          </div>
        )}

        {/* Host-Firewall (firewalld) — nur relevantes anzeigen */}
        {fw && (
          fw.allOpen ? (
            <div className="flex items-center gap-2 rounded-lg bg-online/10 border border-online/20 px-3 py-2 text-xs text-online">
              <ShieldCheck size={15} className="shrink-0" /> Alle benötigten Ports sind in der Host-Firewall freigegeben.
            </div>
          ) : fw.command ? (
            <div className="rounded-lg bg-warn/10 border border-warn/25 p-3 space-y-2">
              <div className="flex items-center gap-2 text-xs text-warn font-medium">
                <ShieldAlert size={15} className="shrink-0" />
                {fw.checked
                  ? `${fw.missing.length} Port(s) sind auf dem Server noch nicht freigegeben.`
                  : "Firewall-Status nicht prüfbar — bei Bedarf freigeben:"}
              </div>
              <div className="flex items-center gap-2">
                <code className="flex-1 rounded-md bg-black/40 border border-line px-2.5 py-2 font-mono text-[11px] text-fg overflow-x-auto whitespace-nowrap">{fw.command}</code>
                <Button variant="secondary" onClick={() => copyText(fw.command, setCmdCopied)} title="Befehl kopieren">
                  {cmdCopied ? <Check size={15} className="text-online" /> : <Copy size={15} />}
                </Button>
              </div>
              <p className="text-[11px] text-fg-muted">Einmal im Terminal des Servers ausführen (fragt nach deinem Passwort). Danach ist der Port dauerhaft offen.</p>
            </div>
          ) : null
        )}

        {game?.slug === "sevendtd" && (
          <p className="text-[11px] text-fg-muted">
            Steam-Relay ist aktiv: Beitritt über den Steam-Serverbrowser oder die Freundesliste funktioniert
            auch ohne Portfreigabe. Nur der direkte IP-Beitritt braucht die Freigaben oben.
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export const SteamTab = observer(() => {
  const server = serverStore.activeServer;
  const [meta, setMeta] = useState<any>(null);
  const [game, setGame] = useState<any>(null);
  const [cfg, setCfg] = useState<any>({});
  const [serverPort, setServerPort] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const install = server ? serverStore.activeInstalls.get(server.id) : undefined;
  const busy = install && install.status !== "complete" && install.status !== "error";

  const load = async () => {
    if (!server) return;
    setLoading(true);
    const data = await serverStore.fetchSteamConfig(server.id);
    if (data?.meta) {
      setMeta(data.meta);
      setGame(data.game);
      setCfg(data.meta.config || {});
      setServerPort(data.serverPort ?? null);
    }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [server?.id]);
  // Reload once an install finishes so status/version refresh.
  useEffect(() => { if (install?.status === "complete") load(); /* eslint-disable-next-line */ }, [install?.status]);

  if (!server) return null;
  if (loading) return <div className="flex items-center gap-2 text-fg-muted py-10"><Loader2 className="animate-spin" size={18} /> Lade Steam-Konfiguration…</div>;

  const appId = meta?.appId;
  const hero = heroFor(meta?.storeAppId || meta?.appId);

  const reinstall = async () => {
    await serverStore.steamInstall(server.id, { slug: meta.slug, appId: meta.appId, config: cfg, restart: false });
    toast.success("Installation/Update gestartet…");
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Left: game + install */}
      <div className="lg:col-span-1 space-y-6">
        <Card className="bg-ink border-line-strong text-white overflow-hidden" noPadding>
          {hero && <img src={hero} alt={meta?.gameName} className="w-full h-28 object-cover" onError={(e) => ((e.target as HTMLImageElement).style.display = "none")} />}
          <CardContent className="space-y-4 pt-4">
            <div className="flex items-center gap-2">
              <Gamepad2 size={18} className="text-ember" />
              <div>
                <div className="font-display font-semibold text-fg leading-tight">{meta?.gameName || "Steam-Server"}</div>
                <div className="text-xs text-fg-muted font-mono">AppID {appId ?? "—"}</div>
              </div>
            </div>

            {busy ? (
              <div>
                <div className="flex justify-between text-xs text-fg-muted mb-1">
                  <span>{install?.message || "Installation läuft…"}</span>
                  <span className="font-mono">{Math.round(install?.progress || 0)}%</span>
                </div>
                <div className="h-2 rounded-full bg-white/[0.06] overflow-hidden">
                  <div className="h-full bg-ember transition-all duration-500" style={{ width: `${install?.progress || 0}%` }} />
                </div>
              </div>
            ) : install?.status === "error" ? (
              <div className="rounded-lg bg-red-500/10 border border-red-500/20 p-3 text-xs text-red-300 flex gap-2">
                <AlertTriangle size={14} className="shrink-0 mt-0.5" /> {install.message}
              </div>
            ) : (
              <div className="text-xs text-fg-muted">
                {meta?.installedAt ? `Installiert am ${new Date(meta.installedAt).toLocaleDateString("de-DE")}.` : "Noch nicht installiert."}
              </div>
            )}

            <Button className="w-full" onClick={reinstall} disabled={!!busy}>
              {busy ? <Loader2 className="animate-spin mr-2" size={16} /> : <Download className="mr-2" size={16} />}
              {meta?.installedAt ? "Aktualisieren / Neu installieren" : "Installieren"}
            </Button>
            <p className="text-[11px] text-fg-muted">Große Spiele (z.B. ARK ~15 GB) brauchen etwas — der Fortschritt läuft live.</p>
          </CardContent>
        </Card>

        <ConnectCard game={game} serverId={server.id} serverPort={serverPort} />
      </div>

      {/* Right: config + mods */}
      <div className="lg:col-span-2 space-y-6">
        {/* Name, Passwort, Map & Co. wohnen jetzt gesammelt unter „Einstellungen" (2026-09-14). */}
        <Card className="bg-ink border-line-strong text-white" noPadding>
          <CardContent className="pt-5 flex flex-wrap items-center justify-between gap-4">
            <div className="min-w-0">
              <div className="font-display font-semibold text-fg">Server-Einstellungen</div>
              <p className="text-xs text-fg-muted mt-1">
                Name im Serverbrowser, Passwort, Karte und max. Spieler findest du jetzt gesammelt unter „Einstellungen".
              </p>
            </div>
            <Button variant="secondary" onClick={() => navigate(`/server/${server.id}/settings`, { state: { section: "spiel" } })}>
              <Settings size={15} /> Zu den Einstellungen
            </Button>
          </CardContent>
        </Card>

        <SteamModBrowser serverId={server.id} meta={meta} onReload={load} />
      </div>
    </div>
  );
});

export default SteamTab;
